import { useRef, useState } from 'react'
import { api, errorMessage } from '../api/client'
import { Amt, Card, ConfirmDelete, Dt, Empty, Note, PageHead, TotalBox } from '../components/ui'
import { useAuth } from '../auth/AuthContext'
import { useApi } from '../hooks/useApi'
import { today } from '../lib/format'
import { shop } from '../lib/shop'

interface BinEntry {
  id: string
  kind: string
  kindLabel: string
  description?: string
  date?: string
  amount?: string
  deletedAt: string
  daysLeft: number
}

interface BackupSummary {
  customers: number
  vendors: number
  sales: number
  payments: number
  supplies: number
  totalSales: string
  totalPayments: string
}

interface RestoreReport {
  customers: number
  vendors: number
  sales: number
  payments: number
  supplies: number
  lotSales: number
  expenses: number
  checksVerified: number
}

/**
 * The مزید drawer: keeping a copy of the book, putting one back, the bin, and
 * the one button that empties everything.
 */
export function Tools() {
  const { isOwner } = useAuth()
  const summary = useApi<BackupSummary>('/backup/summary')
  const bin = useApi<BinEntry[]>('/backup/bin')

  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [wipeWord, setWipeWord] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  function refresh() {
    summary.reload()
    bin.reload()
  }

  /**
   * The export needs the bearer token, so it cannot be a plain link - the
   * browser would fetch it without the header and get a 401. It comes back as a
   * blob and is handed to a download the same way a bill picture is.
   */
  async function downloadBackup() {
    setError(null)
    setBusy(true)
    try {
      const response = await api.get('/backup/export', { responseType: 'blob' })
      const url = URL.createObjectURL(response.data as Blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${shop.slug}-backup-${today()}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setTimeout(() => URL.revokeObjectURL(url), 10_000)
      setDone('بیک اپ فائل محفوظ ہو گئی۔ اِسے کسی محفوظ جگہ رکھیں۔')
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  async function restoreBackup(file: File) {
    setError(null)
    setDone(null)
    setBusy(true)
    try {
      const form = new FormData()
      form.append('file', file)
      const response = await api.post<RestoreReport>('/backup/restore', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      const report = response.data
      setDone(
        `پورا حساب واپس آ گیا — ${report.customers} گاہک، ${report.vendors} زمیندار، ` +
          `${report.sales} سودے، ${report.supplies} سپلائی۔ فائل کے ${report.checksVerified} ہندسے ملا کر دیکھے گئے۔`,
      )
      refresh()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  async function restoreOne(entry: BinEntry) {
    setError(null)
    try {
      await api.post(`/backup/bin/${entry.kind}/${entry.id}/restore`)
      setDone(`${entry.kindLabel} واپس آ گئی۔`)
      refresh()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  async function wipeEverything() {
    setError(null)
    setBusy(true)
    try {
      await api.post('/backup/wipe', { confirm: wipeWord })
      setDone('سب کچھ مٹا دیا گیا۔')
      setWipeWord('')
      refresh()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHead title="مزید" hint="بیک اپ، واپسی، ڈیلیٹ کی ہوئی انٹریاں، اور سب کچھ مٹانا۔" />

      {error && <Note kind="error">{error}</Note>}
      {done && <Note kind="info">{done}</Note>}

      {summary.data && (
        <div className="totals">
          <TotalBox label="گاہک" value={<span className="amt">{summary.data.customers}</span>} />
          <TotalBox label="زمیندار" value={<span className="amt">{summary.data.vendors}</span>} />
          <TotalBox label="کل سودے" value={<Amt value={summary.data.totalSales} />} />
          <TotalBox label="کل وصولی" value={<Amt value={summary.data.totalPayments} />} />
        </div>
      )}

      <div className="grid grid--2">
        <Card title="بیک اپ رکھیں">
          <p className="muted small" style={{ marginTop: 0 }}>
            پورا حساب ایک CSV فائل میں۔ یہ فائل ایکسل میں بھی کھل جاتی ہے، اور اِسی سے حساب واپس بھی
            آ سکتا ہے۔ ہفتے میں ایک بار رکھ لیا کریں اور فون یا ای میل پر بھیج دیا کریں۔
          </p>
          <button type="button" className="btn" onClick={downloadBackup} disabled={busy}>
            {busy ? 'بن رہی ہے…' : 'بیک اپ فائل محفوظ کریں'}
          </button>
        </Card>

        <Card title="بیک اپ سے واپس لائیں">
          {isOwner ? (
            <>
              <p className="muted small" style={{ marginTop: 0 }}>
                فائل کے اندر کا حساب پہلے جوڑ کر دیکھا جاتا ہے۔ اگر ایک روپے کا بھی فرق نکلا تو کچھ
                بھی نہیں بدلا جاتا اور فرق بتا دیا جاتا ہے۔
              </p>
              <Note kind="warn">
                واپسی سے موجودہ سارا حساب مٹ کر فائل والا آ جائے گا۔ پہلے آج کا بیک اپ رکھ لیں۔
              </Note>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                disabled={busy}
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  if (file) void restoreBackup(file)
                }}
              />
            </>
          ) : (
            <Note kind="warn">یہ کام صرف مالک کر سکتے ہیں۔</Note>
          )}
        </Card>
      </div>

      <Card
        title={`ڈیلیٹ کی ہوئی انٹریاں (${bin.data?.length ?? 0})`}
        actions={<span className="muted small">30 دن تک واپس آ سکتی ہیں</span>}
      >
        <p className="muted small" style={{ marginTop: 0 }}>
          اِس ایپ میں کوئی چیز فوراً مٹتی نہیں۔ ڈیلیٹ کی ہوئی انٹری حساب سے تو نکل جاتی ہے مگر یہاں
          پڑی رہتی ہے، اور ایک ٹچ سے واپس آ جاتی ہے۔
        </p>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>کیا تھا</th>
                <th>تفصیل</th>
                <th>تاریخ</th>
                <th>رقم</th>
                <th>کب ڈیلیٹ ہوئی</th>
                <th>کتنے دن باقی</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(bin.data ?? []).map((entry) => (
                <tr key={`${entry.kind}-${entry.id}`}>
                  <td>{entry.kindLabel}</td>
                  <td>{entry.description ?? '—'}</td>
                  <td>{entry.date ? <Dt value={entry.date} /> : '—'}</td>
                  <td className="num">{entry.amount ? <Amt value={entry.amount} /> : '—'}</td>
                  <td>
                    <Dt value={entry.deletedAt.slice(0, 10)} />
                  </td>
                  <td className="num">
                    <span className="amt">{entry.daysLeft}</span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn--ghost btn--small"
                      onClick={() => void restoreOne(entry)}
                    >
                      واپس لائیں
                    </button>
                  </td>
                </tr>
              ))}

              {(bin.data?.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={7}>
                    <Empty>بن خالی ہے — کچھ بھی ڈیلیٹ نہیں کیا گیا۔</Empty>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="حساب کی حفاظت">
        <ul className="muted small" style={{ margin: 0, paddingInlineStart: 20 }}>
          <li>ہفتے میں ایک بار بیک اپ فائل محفوظ کر کے اپنے فون یا ای میل پر بھیج دیں۔</li>
          <li>پن کسی کو نہ بتائیں۔ ضرورت ہو تو نیا اکاؤنٹ بنوا لیں۔</li>
          <li>ڈیلیٹ کی ہوئی انٹری 30 دن تک اوپر والی لسٹ سے واپس آ سکتی ہے۔</li>
          <li>«سب کچھ مٹائیں» کے بعد کچھ واپس نہیں آتا — صرف بیک اپ فائل سے آ سکتا ہے۔</li>
        </ul>
      </Card>

      {isOwner && (
        <Card title="سب کچھ مٹائیں">
          <Note kind="error">
            اِس سے سارے گاہک، زمیندار، سودے، سپلائی، خرچہ — سب مٹ جائے گا اور واپس نہیں آئے گا۔ صرف
            بیک اپ فائل سے واپس آ سکتا ہے۔ پہلے بیک اپ رکھ لیں۔
          </Note>

          <p className="muted small">
            پکا کرنے کے لیے نیچے انگریزی میں <strong>DELETE</strong> لکھیں۔
          </p>

          <div className="row">
            <div className="field" style={{ maxWidth: 220 }}>
              <label>DELETE</label>
              <input
                value={wipeWord}
                onChange={(event) => setWipeWord(event.target.value)}
                style={{ direction: 'ltr', textAlign: 'left', letterSpacing: '0.2em' }}
              />
            </div>
            <div className="field" style={{ maxWidth: 220 }}>
              <label>&nbsp;</label>
              {wipeWord === 'DELETE' ? (
                <ConfirmDelete onConfirm={() => void wipeEverything()} label="سب کچھ مٹائیں" />
              ) : (
                <button type="button" className="btn btn--danger" disabled>
                  سب کچھ مٹائیں
                </button>
              )}
            </div>
          </div>
        </Card>
      )}
    </>
  )
}
